// ─── MODAL: GERAR VENDA ───────────
import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';

export function ModalGerarVenda({ propostaId, tituloNegocio, statusAtual, gemeo = false, onClose, onGerada }: {
  propostaId: number;
  tituloNegocio: string;
  statusAtual?: string; // L18 — status atual do negócio (pra decidir default de marcarGanho)
  // Gêmeo da licitação (06/10/2026): a etapa acompanha a licitação — a venda sai
  // SEM marcar ganho (o servidor responde 409 ao "Ganhar" nele).
  gemeo?: boolean;
  onClose: () => void;
  onGerada: () => void;
}) {
  const [propostas, setPropostas] = useState<any[]>([]);
  // Multi-seleção: gera UMA venda por proposta selecionada.
  const [propostasSelecionadas, setPropostasSelecionadas] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [gerando, setGerando] = useState(false);
  const [progresso, setProgresso] = useState<{ feitas: number; total: number } | null>(null);
  // Sprint UI: preset "sem capa" — passa mostrar_capa=false ao criar a venda.
  // Default true (sem capa) porque vendas geralmente são contratos sucintos,
  // diferente da proposta que tem capa de apresentação.
  const [semCapa, setSemCapa] = useState(true);
  // L18 (2026-06-03): permitir gerar venda SEM mover o card pra Ganho.
  // Default ON pra preservar fluxo antigo, mas o vendedor pode desmarcar
  // pra emitir o doc agora e marcar Ganho depois (caso comum em licitação:
  // emite venda na hora da contratação, mas espera homologação pra fechar).
  const [marcarGanho, setMarcarGanho] = useState(true);
  // Se ja esta ganho, nao adianta marcar de novo — toggle some.
  const jaEstaGanho = statusAtual === 'ganho';
  const ganhar = marcarGanho && !jaEstaGanho && !gemeo;

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const res = await axios.get(`${API_URL}/comercial/propostas-geradas?proposta_id=${propostaId}`,
          { headers: { Authorization: token } });
        const data = res.data.body ? JSON.parse(res.data.body) : res.data;
        const props = Array.isArray(data) ? data.filter((d: any) => !d.tipo || d.tipo === 'proposta') : [];
        setPropostas(props);
        // Default da seleção:
        // - 1 proposta → marca ela
        // - >=1 aprovada/aceita → marca todas as aprovadas
        // - nenhuma aprovada → marca a mais recente
        const aprovadas = props.filter((p: any) => p.status === 'aprovada');
        const inicial = new Set<string>();
        if (props.length === 1) inicial.add(props[0].id);
        else if (aprovadas.length > 0) aprovadas.forEach((p: any) => inicial.add(p.id));
        else if (props.length > 0) inicial.add(props[0].id);
        setPropostasSelecionadas(inicial);
      } finally { setLoading(false); }
    })();
  }, [propostaId]);

  function toggleProposta(id: string) {
    setPropostasSelecionadas(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function gerar() {
    setGerando(true);
    setProgresso(null);
    try {
      const token = await getToken();
      const ids = Array.from(propostasSelecionadas);

      // Caso 1: nenhuma selecionada e existe propostas → user precisa decidir
      if (propostas.length > 0 && ids.length === 0) {
        const semBase = window.confirm(
          'Nenhuma proposta selecionada. Gerar 1 documento de venda em branco mesmo assim?'
        );
        if (!semBase) { setGerando(false); return; }
        await axios.post(`${API_URL}/comercial/vendas/gerar`, {
          proposta_id: propostaId, proposta_gerada_base_id: null,
          mostrar_capa: !semCapa,
          marcar_ganho: ganhar,
        }, { headers: { Authorization: token } });
        onGerada();
        return;
      }

      // Caso 2: nenhuma proposta no negócio → gera 1 venda em branco
      if (propostas.length === 0) {
        await axios.post(`${API_URL}/comercial/vendas/gerar`, {
          proposta_id: propostaId, proposta_gerada_base_id: null,
          mostrar_capa: !semCapa,
          marcar_ganho: ganhar,
        }, { headers: { Authorization: token } });
        onGerada();
        return;
      }

      // Caso 3: 1+ propostas selecionadas → gera 1 venda por proposta
      const erros: string[] = [];
      for (let i = 0; i < ids.length; i++) {
        setProgresso({ feitas: i, total: ids.length });
        try {
          // Até 06/10/2026 este caso não mandava `marcar_ganho` e o servidor
          // marcava ganho mesmo com a caixa desmarcada. Negócio já ganho segue
          // sem mandar (o servidor completa os créditos que faltarem, como antes).
          await axios.post(`${API_URL}/comercial/vendas/gerar`, {
            proposta_id: propostaId, proposta_gerada_base_id: ids[i],
            mostrar_capa: !semCapa,
            ...(jaEstaGanho ? {} : { marcar_ganho: ganhar }),
          }, { headers: { Authorization: token } });
        } catch (e: any) {
          const p = propostas.find(x => x.id === ids[i]);
          erros.push(`${p?.numero_proposta || ids[i].slice(0,8)}: ${e.response?.data?.error || e.message}`);
        }
      }
      setProgresso({ feitas: ids.length, total: ids.length });
      if (erros.length > 0) {
        alert(`${ids.length - erros.length}/${ids.length} vendas geradas.\n\nErros:\n${erros.join('\n')}`);
      }
      onGerada();
    } catch (e: any) {
      alert('Erro ao gerar venda: ' + (e.response?.data?.error || e.message));
    } finally { setGerando(false); }
  }

  const nSelecionadas = propostasSelecionadas.size;
  // L18 — texto adapta ao estado de marcarGanho e jaEstaGanho
  const prefixo = ganhar ? '✓ Marcar como Ganho e ' : '';
  const labelBotao = gerando
    ? (progresso ? `Gerando ${progresso.feitas + 1}/${progresso.total}...` : 'Gerando...')
    : nSelecionadas === 0
      ? (ganhar ? '✓ Marcar como Ganho (sem venda)' : 'Sem ação')
      : nSelecionadas === 1
        ? `${prefixo}gerar 1 venda`
        : `${prefixo}gerar ${nSelecionadas} vendas`;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1500,
    }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '92%', maxWidth: 600,
        maxHeight: '85vh', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 6px', fontSize: 16, color: '#0a7f4a' }}>
          {ganhar ? '✓ Marcar como Ganho — ' : ''}Gerar documento(s) de venda
        </h3>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 16 }}>
          Negócio: <strong>{tituloNegocio}</strong>
        </div>

        {loading ? (
          <div style={{ padding: 20, textAlign: 'center', color: '#888' }}>Carregando propostas...</div>
        ) : (
          <>
            {propostas.length === 0 ? (
              <div style={{
                padding: 12, background: '#FFF8E8', border: '0.5px solid #B07500',
                borderRadius: 6, fontSize: 12, color: '#555', marginBottom: 14,
              }}>
                ⚠ Nenhuma proposta gerada para este negócio. O documento de venda será criado em branco —
                você preenche depois na aba <strong>Vendas</strong>.
              </div>
            ) : (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 4 }}>
                  Selecione as propostas que foram ganhas{propostas.length > 1 ? ' (uma venda será gerada para cada)' : ''}
                </div>
                <div style={{ fontSize: 10, color: '#888', marginBottom: 10 }}>
                  Cada proposta vira um documento de venda separado, com numeração VEND-AAAA-NNNN própria.
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 6 }}>
                  {propostas.map(p => {
                    const selecionada = propostasSelecionadas.has(p.id);
                    const aprovada = p.status === 'aprovada';
                    return (
                      <label key={p.id}
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px',
                          border: `1px solid ${selecionada ? '#0a7f4a' : '#e0e0e0'}`,
                          background: selecionada ? '#E8F5EE' : '#fff',
                          borderRadius: 6, cursor: 'pointer', transition: 'all 0.15s',
                        }}>
                        <input type="checkbox" checked={selecionada}
                          onChange={() => toggleProposta(p.id)}
                          style={{ marginTop: 3, accentColor: '#0a7f4a', cursor: 'pointer' }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#020D7F', marginBottom: 2 }}>
                            {p.numero_proposta}
                            <span style={{ fontWeight: 400, color: '#888', marginLeft: 6 }}>
                              v{p.versao}{p.revisao > 0 ? ` · R${p.revisao}` : ''}
                            </span>
                            {aprovada && (
                              <span style={{
                                marginLeft: 8, fontSize: 9, padding: '1px 7px', borderRadius: 3,
                                background: '#0a7f4a', color: '#fff', fontWeight: 600,
                              }}>✓ Aceita</span>
                            )}
                          </div>
                          <div style={{ fontSize: 11, color: '#555', lineHeight: 1.4 }}>
                            {p.titulo}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
                <div style={{ fontSize: 10, color: '#888', marginTop: 6 }}>
                  {nSelecionadas === 0
                    ? '⚠ Nenhuma selecionada — marca como Ganho mas não gera venda.'
                    : `${nSelecionadas} de ${propostas.length} selecionada${nSelecionadas !== 1 ? 's' : ''}.`}
                </div>
              </div>
            )}

            {/* Preset: sem capa */}
            <label style={{
              display: 'flex', alignItems: 'center', gap: 8, marginTop: 14,
              padding: '8px 12px', background: '#FAFBFF', border: '0.5px solid #d8dcec',
              borderRadius: 5, cursor: 'pointer', fontSize: 12,
            }}>
              <input type="checkbox" checked={semCapa}
                onChange={e => setSemCapa(e.target.checked)}
                style={{ cursor: 'pointer' }} />
              <strong>Sem página de capa</strong>
              <span style={{ color: '#888', fontSize: 11 }}>
                — vendas costumam ser sucintas (contratos). Pode reverter no editor depois.
              </span>
            </label>

            {gemeo && !jaEstaGanho && (
              <div style={{
                marginTop: 8, padding: '8px 12px', background: '#FFF8E8',
                border: '0.5px solid #B07500', borderRadius: 5, fontSize: 12, color: '#555',
              }}>
                <strong>Negócio de licitação:</strong> o documento é gerado sem mover o negócio.
                Ele vira ganho quando a licitação chegar a "Contrato assinado"
                (Comercial › Licitações › Pipeline).
              </div>
            )}

            {/* L18 (2026-06-03): toggle pra marcar como Ganho ou nao */}
            {!jaEstaGanho && !gemeo && (
              <label style={{
                display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 8,
                padding: '8px 12px', background: marcarGanho ? '#E6F0EA' : '#FFF8E8',
                border: `0.5px solid ${marcarGanho ? '#0a7f4a' : '#B07500'}`,
                borderRadius: 5, cursor: 'pointer', fontSize: 12,
              }}>
                <input type="checkbox" checked={marcarGanho}
                  onChange={e => setMarcarGanho(e.target.checked)}
                  style={{ cursor: 'pointer', marginTop: 2 }} />
                <div>
                  <strong>{marcarGanho ? 'Marcar negócio como Ganho' : 'NÃO mover pra Ganho'}</strong>
                  <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>
                    {marcarGanho
                      ? 'O card vai pra coluna "Fechamento" e a probabilidade vira 100%.'
                      : 'Gera o documento sem mover o card. Útil pra licitação: emite venda agora, espera homologação pra fechar.'}
                  </div>
                </div>
              </label>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
              <button onClick={onClose} disabled={gerando}
                style={{ padding: '8px 18px', fontSize: 12, border: '1px solid #ddd', borderRadius: 5,
                  background: '#fff', cursor: gerando ? 'wait' : 'pointer' }}>
                Cancelar
              </button>
              <button onClick={gerar} disabled={gerando}
                style={{ padding: '8px 22px', fontSize: 12, border: 'none', borderRadius: 5,
                  background: '#0a7f4a', color: '#fff', cursor: gerando ? 'wait' : 'pointer', fontWeight: 600 }}>
                {labelBotao}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
